import type {
  LaravelReport,
  LaravelReportsResponse,
  LaravelSessionResponse,
} from '../types/laravel';
import type { ErrorEnvelope } from '../types/envelope';
import { ApiError, NetworkError, SessionExpiredError } from './ai-api.service';

/** Function that returns the current host JWT (sync or async). */
export type AuthTokenProvider = () => string | Promise<string>;

export interface LaravelClientOptions {
  /** Full URL to the reports list endpoint, e.g.
   * `http://parceiro1.dev.local:8000/api/auth/reports/all/by/userlogged?origem=ia` */
  reportsUrl: string;
  /** Full URL to the start-session endpoint, e.g.
   * `http://parceiro1.dev.local:8000/api/auth/ia/chat_session/start` */
  sessionStartUrl: string;
  /** Provider that returns the current JWT — called before every request. */
  getAuthToken: AuthTokenProvider;
}

/** Thrown when the host returned a session-creation error. */
export class SessionStartError extends Error {
  constructor(message: string, public readonly httpStatus: number) {
    super(message);
    this.name = 'SessionStartError';
  }
}

export class LaravelClient {
  constructor(private readonly opts: LaravelClientOptions) {}

  async listReports(): Promise<LaravelReport[]> {
    const data = await this.request<LaravelReportsResponse>('GET', this.opts.reportsUrl);
    const all = Array.isArray(data?.reports) ? data.reports : [];
    // Filter out incomplete entries — some rows in the response have null IDs
    return all.filter(
      (r) => !!r.report_id && !!r.workspace_id && !!r.dataset_id && !!r.token,
    );
  }

  async startSession(reportToken: string): Promise<LaravelSessionResponse> {
    return this.request<LaravelSessionResponse>(
      'POST',
      this.opts.sessionStartUrl,
      { token: reportToken },
    );
  }

  private async request<T>(
    method: 'GET' | 'POST',
    url: string,
    body?: unknown,
  ): Promise<T> {
    const token = await this.opts.getAuthToken();
    if (!token) {
      throw new SessionExpiredError('invalid');
    }
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        credentials: 'omit',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new NetworkError(err);
    }

    if (response.ok) {
      return (await response.json()) as T;
    }

    if (response.status === 401) {
      throw new SessionExpiredError('expired');
    }

    // Try to surface the host's error message — multiple shapes possible
    const raw = await response.text();
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // not JSON, leave as null
    }
    const message = extractErrorMessage(parsed) ?? `HTTP ${response.status}`;

    if (method === 'POST' && url === this.opts.sessionStartUrl) {
      throw new SessionStartError(message, response.status);
    }
    throw new ApiError(message, asString((parsed as ErrorEnvelope | null)?.error_code) ?? 'INTERNAL');
  }
}

function extractErrorMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const obj = payload as Record<string, unknown>;
  // AI API envelope shape: { type, content, error_code }
  if (typeof obj.content === 'string') return obj.content;
  // Laravel wrap: { status, message: string | { content } }
  if (typeof obj.message === 'string') return obj.message;
  if (obj.message && typeof obj.message === 'object') {
    const inner = obj.message as Record<string, unknown>;
    if (typeof inner.content === 'string') return inner.content;
    if (typeof inner.message === 'string') return inner.message;
  }
  if (typeof obj.error === 'string') return obj.error;
  return null;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}
